import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { initialBlogs } from '../data/blogs';
import AmazonStorefront from '../components/AmazonStorefront';

const { FiCalendar, FiClock, FiArrowLeft, FiShare2, FiBookmark } = FiIcons;

function BlogPost() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);

  useEffect(() => {
    const savedBlogs = JSON.parse(localStorage.getItem('user_blogs') || '[]');
    const allBlogs = [...initialBlogs, ...savedBlogs];
    const found = allBlogs.find(b => b.id === id);
    if (found) {
      setBlog(found);
      window.scrollTo(0, 0);
    } else {
      navigate('/blog');
    }
  }, [id, navigate]);

  const formatInline = (text) => {
    if (!text) return text;
    // Bold: **text**
    let parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i} className="font-black text-slate-900">{part.slice(2, -2)}</strong>;
      }
      // Italic: *text*
      let subParts = part.split(/(\*.*?\*)/g);
      return subParts.map((subPart, j) => {
        if (subPart.startsWith('*') && subPart.endsWith('*')) {
          return <em key={`${i}-${j}`} className="italic text-slate-700">{subPart.slice(1, -1)}</em>;
        }
        return subPart;
      });
    });
  };

  const renderContent = (content) => {
    if (!content) return null;
    return content.split('\n\n').map((block, i) => {
      const trimmed = block.trim();
      if (trimmed.startsWith('![')) {
        const match = trimmed.match(/!\[(.*?)\]\((.*?)\)/);
        if (match) {
          return (
            <div key={i} className="my-10">
              <div className="rounded-[2rem] overflow-hidden shadow-2xl border border-slate-100">
                <img src={match[2]} alt={match[1]} className="w-full h-auto" />
              </div>
              {match[1] && <p className="text-center text-xs font-bold text-slate-400 mt-4 uppercase tracking-widest">{match[1]}</p>}
            </div>
          );
        }
      }

      const lines = block.split('\n').filter(l => l.trim());
      const isOL = lines.length > 1 && lines.every(l => /^\d+\.\s/.test(l.trim()));
      const isUL = lines.length > 1 && lines.every(l => /^[-•]\s/.test(l.trim()));

      if (isOL || isUL) {
        const Tag = isOL ? 'ol' : 'ul';
        return (
          <Tag key={i} className={`${isOL ? 'list-decimal' : 'list-disc'} ml-8 mb-8 space-y-3 text-slate-700`}>
            {lines.map((line, idx) => (
              <li key={idx} className="pl-2">{formatInline(line.replace(/^(\d+\.\s+|[-•]\s+)/, ''))}</li>
            ))}
          </Tag>
        );
      }

      return <p key={i} className="mb-6 leading-relaxed text-slate-800">{formatInline(block)}</p>;
    });
  };

  if (!blog) return null;

  return (
    <div className="bg-white min-h-screen pb-20">
      <div className="relative h-[60vh] min-h-[400px] overflow-hidden">
        <img src={blog.image} alt={blog.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex flex-col justify-end pb-12 md:pb-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="mb-6">
                <span className="bg-indigo-600 text-white px-4 py-1.5 rounded-full text-[11px] font-black uppercase tracking-[0.2em] shadow-lg">
                  {blog.category}
                </span>
              </div>
              <h1 className="text-4xl md:text-6xl font-black text-white mb-6 leading-tight tracking-tighter">
                {blog.title}
              </h1>
              <div className="flex flex-wrap items-center gap-6 text-white/80 text-[12px] font-bold uppercase tracking-widest">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center text-white">
                    {blog.author?.charAt(0) || 'A'}
                  </div>
                  <span>{blog.author}</span>
                </div>
                <div className="flex items-center gap-2">
                  <SafeIcon icon={FiCalendar} className="w-4 h-4" />
                  <span>{blog.date}</span>
                </div>
                <div className="flex items-center gap-2">
                  <SafeIcon icon={FiClock} className="w-4 h-4" />
                  <span>{blog.readTime} Read</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 -mt-10 relative z-10">
        <div className="max-w-4xl mx-auto bg-white rounded-[40px] shadow-2xl shadow-slate-200/50 p-8 md:p-16 border border-slate-50">
          <div className="flex items-center justify-between mb-12 pb-8 border-b border-slate-100">
            <Link to="/blog" className="flex items-center gap-2 text-slate-400 hover:text-indigo-600 font-bold text-xs uppercase tracking-widest transition-colors">
              <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
              <span>Back to Library</span>
            </Link>
            <div className="flex items-center gap-4">
              <button className="p-3 hover:bg-slate-50 rounded-full text-slate-400 hover:text-indigo-600 transition-all"><SafeIcon icon={FiShare2} className="w-5 h-5" /></button>
              <button className="p-3 hover:bg-slate-50 rounded-full text-slate-400 hover:text-indigo-600 transition-all"><SafeIcon icon={FiBookmark} className="w-5 h-5" /></button>
            </div>
          </div>

          <div className="prose prose-lg max-w-none">
            {blog.excerpt && (
              <p className="text-xl md:text-2xl text-slate-600 leading-relaxed font-serif italic mb-10 border-l-4 border-indigo-500 pl-8">
                "{blog.excerpt}"
              </p>
            )}
            <div className="text-slate-800 leading-[1.8] text-lg">
              {renderContent(blog.content)}
            </div>
          </div>

          <AmazonStorefront title="Featured Products" layout="grid" />

          <div className="mt-16 pt-12 border-t border-slate-100">
            <div className="bg-slate-50 rounded-3xl p-8 flex flex-col md:flex-row items-center gap-8 border border-slate-100/50">
              <div className="w-24 h-24 rounded-full bg-indigo-600 flex items-center justify-center text-white text-3xl font-black">{blog.author?.charAt(0) || 'A'}</div>
              <div className="flex-1 text-center md:text-left">
                <h4 className="text-xl font-bold text-slate-900 mb-2">About {blog.author}</h4>
                <p className="text-slate-500 text-sm">A contributing writer at Quote Poster, specializing in {blog.category?.toLowerCase() || 'design'}.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BlogPost;